import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { companyService } from '../../services/company.service';
import type { Company, CreateCompanyRequest, UpdateCompanyRequest, CompanyFilter } from '../../services/company.service';
import { USER_ROLES } from '../../constants';
import { usePagination } from '../../hooks';
import { toast } from '../../components/Toast';
import styles from './Companies.module.css';

const COMPANY_TYPES: Record<number, string> = {
  0: 'Ristorante',
  1: 'Bar',
  2: 'Hotel',
  3: 'Caffetteria',
  99: 'Altro',
};

const SUBSCRIPTION_PLANS: Record<number, string> = {
  0: 'Free',
  1: 'Basic',
  2: 'Premium',
  3: 'Enterprise',
};

const DEFAULT_PAGE_SIZE = 20;

export const Companies: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  // Use pagination custom hook
  const { page, totalPages, setTotalPages, resetPage, nextPage, previousPage, isFirstPage, isLastPage, hasMultiplePages } = usePagination();

  // Filtri
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('');
  const [typeFilter, setTypeFilter] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 0,
    vatNumber: '',
    address: '',
    city: '',
    subscriptionPlan: 0,
    tenantCode: '',
    loyaltyEnabled: false,
    // Owner fields (only for create)
    ownerEmail: '',
    ownerFirstName: '',
    ownerLastName: '',
    ownerPassword: '',
  });

  // Memoize loadCompanies to prevent recreation on every render
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filter: CompanyFilter = { page, pageSize: DEFAULT_PAGE_SIZE };

      if (activeFilter !== '') {
        filter.isActive = activeFilter;
      }

      if (typeFilter !== '') {
        filter.type = typeFilter;
      }

      if (searchTerm) {
        filter.search = searchTerm;
      }

      const response = await companyService.getCompanies(filter);
      setCompanies(response.data);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento delle aziende';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter, typeFilter, searchTerm, setTotalPages]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Memoize initial form data
  const initialFormData = useMemo(
    () => ({
      name: '',
      type: 0,
      vatNumber: '',
      address: '',
      city: '',
      subscriptionPlan: 0,
      tenantCode: '',
      loyaltyEnabled: false,
      ownerEmail: '',
      ownerFirstName: '',
      ownerLastName: '',
      ownerPassword: '',
    }),
    []
  );

  // Memoize handleSearch
  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      resetPage();
      loadCompanies();
    },
    [resetPage, loadCompanies]
  );

  // Memoize openCreateModal
  const openCreateModal = useCallback(() => {
    setEditingCompany(null);
    setFormData(initialFormData);
    setShowModal(true);
  }, [initialFormData]);

  // Memoize openEditModal
  const openEditModal = useCallback((company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      type: Object.entries(COMPANY_TYPES).find(([, v]) => v === company.type)?.[0]
        ? parseInt(Object.entries(COMPANY_TYPES).find(([, v]) => v === company.type)![0])
        : 0,
      vatNumber: company.vatNumber || '',
      address: company.address || '',
      city: company.city || '',
      subscriptionPlan: Object.entries(SUBSCRIPTION_PLANS).find(([, v]) => v === company.subscriptionPlan)?.[0]
        ? parseInt(Object.entries(SUBSCRIPTION_PLANS).find(([, v]) => v === company.subscriptionPlan)![0])
        : 0,
      tenantCode: company.tenantCode || '',
      loyaltyEnabled: company.loyaltyEnabled,
      // Owner fields not needed for edit
      ownerEmail: '',
      ownerFirstName: '',
      ownerLastName: '',
      ownerPassword: '',
    });
    setShowModal(true);
  }, []);

  // Memoize closeModal
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingCompany(null);
    setFormData(initialFormData);
    setError('');
  }, [initialFormData]);

  // Memoize handleSubmit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      try {
        if (editingCompany) {
          // Update
          const updateData: UpdateCompanyRequest = {
            name: formData.name,
            type: formData.type,
            vatNumber: formData.vatNumber || undefined,
            address: formData.address || undefined,
            city: formData.city || undefined,
            subscriptionPlan: formData.subscriptionPlan,
            tenantCode: formData.tenantCode || undefined,
            loyaltyEnabled: formData.loyaltyEnabled,
          };
          await companyService.updateCompany(editingCompany.id, updateData);
          toast.success('Azienda aggiornata con successo!');
        } else {
          // Create
          const createData: CreateCompanyRequest = {
            name: formData.name,
            type: formData.type,
            vatNumber: formData.vatNumber || undefined,
            address: formData.address || undefined,
            city: formData.city || undefined,
            subscriptionPlan: formData.subscriptionPlan,
            tenantCode: formData.tenantCode || undefined,
            loyaltyEnabled: formData.loyaltyEnabled,
            ownerEmail: formData.ownerEmail,
            ownerFirstName: formData.ownerFirstName,
            ownerLastName: formData.ownerLastName,
            ownerPassword: formData.ownerPassword,
          };
          await companyService.createCompany(createData);
          toast.success('Azienda creata con successo!');
        }
        closeModal();
        loadCompanies();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Errore nel salvataggio dell'azienda";
        setError(message);
      }
    },
    [editingCompany, formData, closeModal, loadCompanies]
  );

  // Memoize handleDeactivate
  const handleDeactivate = useCallback(
    async (id: number, name: string) => {
      if (!window.confirm(`Sei sicuro di voler disattivare l'azienda "${name}"?\n\nTutte le sedi e i dipendenti associati verranno disattivati.`)) {
        return;
      }

      try {
        await companyService.deactivateCompany(id);
        toast.success('Azienda disattivata con successo');
        loadCompanies();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Errore nella disattivazione dell'azienda";
        toast.error(message);
      }
    },
    [loadCompanies]
  );

  // Memoize filter change handlers
  const handleActiveFilterChange = useCallback(
    (value: string) => {
      setActiveFilter(value === '' ? '' : value === 'true');
      resetPage();
    },
    [resetPage]
  );

  const handleTypeFilterChange = useCallback(
    (value: string) => {
      setTypeFilter(value === '' ? '' : parseInt(value));
      resetPage();
    },
    [resetPage]
  );

  // Guard: only SuperAdmin
  if (!currentUser || currentUser.role !== USER_ROLES.SUPER_ADMIN) {
    return (
      <div className={styles.companies}>
        <div className={styles.error}>Accesso non autorizzato. Solo i Super Admin possono gestire le aziende.</div>
      </div>
    );
  }

  return (
    <div className={styles.companies}>
      <div className={styles.header}>
        <h1>🏗️ Gestione Aziende</h1>
        <button className={styles.createButton} onClick={openCreateModal}>
          ➕ Nuova Azienda
        </button>
      </div>

      {/* Filtri e ricerca */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Cerca azienda per nome, owner o P.IVA..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>
            🔍 Cerca
          </button>
        </form>

        <div className={styles.filterGroup}>
          <label htmlFor="active">Stato:</label>
          <select
            id="active"
            value={activeFilter === '' ? '' : activeFilter ? 'true' : 'false'}
            onChange={(e) => handleActiveFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutte</option>
            <option value="true">Attive</option>
            <option value="false">Disattivate</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="type">Tipo:</label>
          <select
            id="type"
            value={typeFilter === '' ? '' : typeFilter}
            onChange={(e) => handleTypeFilterChange(e.target.value)}
            className={styles.select}
          >
            <option value="">Tutti</option>
            {Object.entries(COMPANY_TYPES).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento aziende...</div>
      ) : companies.length === 0 ? (
        <div className={styles.empty}>
          <p>📭 Nessuna azienda trovata</p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Owner</th>
                  <th>Sedi</th>
                  <th>Dipendenti</th>
                  <th>Piano</th>
                  <th>Codice Tenant</th>
                  <th>Loyalty</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td className={styles.companyName}>{company.name}</td>
                    <td>{company.type}</td>
                    <td>
                      {company.ownerName}
                      <br />
                      <small style={{ color: 'var(--text-secondary)' }}>{company.ownerEmail}</small>
                    </td>
                    <td className={styles.center}>{company.siteCount}</td>
                    <td className={styles.center}>{company.employeeCount}</td>
                    <td>{company.subscriptionPlan}</td>
                    <td>{company.tenantCode || '-'}</td>
                    <td>
                      <span className={company.loyaltyEnabled ? styles.badgeLoyaltyOn : styles.badgeLoyaltyOff}>
                        {company.loyaltyEnabled ? '✓ Attiva' : '✗ Off'}
                      </span>
                    </td>
                    <td>
                      <span className={company.isActive ? styles.badgeActive : styles.badgeInactive}>
                        {company.isActive ? '✓ Attiva' : '✗ Disattivata'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.editButton}
                          onClick={() => openEditModal(company)}
                          title="Modifica"
                        >
                          ✏️
                        </button>
                        {company.isActive && (
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeactivate(company.id, company.name)}
                            title="Disattiva"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMultiplePages && (
            <div className={styles.pagination}>
              <button
                onClick={previousPage}
                disabled={isFirstPage}
                className={styles.pageButton}
              >
                ← Precedente
              </button>
              <span className={styles.pageInfo}>
                Pagina {page} di {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={isLastPage}
                className={styles.pageButton}
              >
                Successiva →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal per crea/modifica */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingCompany ? 'Modifica Azienda' : 'Nuova Azienda'}</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Company Info Section */}
              <div className={styles.formSection}>
                <p className={styles.formSectionTitle}>🏢 Informazioni Azienda</p>

                <div className={styles.formGroup}>
                  <label htmlFor="name">Nome Azienda *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="type">Tipo *</label>
                    <select
                      id="type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
                      className={styles.select}
                      required
                    >
                      {Object.entries(COMPANY_TYPES).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="vatNumber">P.IVA</label>
                    <input
                      type="text"
                      id="vatNumber"
                      value={formData.vatNumber}
                      onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="address">Indirizzo</label>
                  <input
                    type="text"
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="city">Città</label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className={styles.input}
                  />
                </div>
              </div>

              {/* Owner Section (only for create) */}
              {!editingCompany ? (
                <div className={styles.formSection}>
                  <p className={styles.formSectionTitle}>👤 Owner Azienda</p>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="ownerFirstName">Nome *</label>
                      <input
                        type="text"
                        id="ownerFirstName"
                        value={formData.ownerFirstName}
                        onChange={(e) => setFormData({ ...formData, ownerFirstName: e.target.value })}
                        className={styles.input}
                        required
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="ownerLastName">Cognome *</label>
                      <input
                        type="text"
                        id="ownerLastName"
                        value={formData.ownerLastName}
                        onChange={(e) => setFormData({ ...formData, ownerLastName: e.target.value })}
                        className={styles.input}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="ownerEmail">Email *</label>
                    <input
                      type="email"
                      id="ownerEmail"
                      value={formData.ownerEmail}
                      onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                      className={styles.input}
                      required
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="ownerPassword">Password *</label>
                    <input
                      type="password"
                      id="ownerPassword"
                      value={formData.ownerPassword}
                      onChange={(e) => setFormData({ ...formData, ownerPassword: e.target.value })}
                      className={styles.input}
                      required
                      minLength={8}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.formSection}>
                  <p className={styles.formSectionTitle}>👤 Owner Azienda</p>
                  <div className={styles.ownerInfo}>
                    Owner: <strong>{editingCompany.ownerName}</strong> ({editingCompany.ownerEmail})
                  </div>
                </div>
              )}

              {/* Subscription & Loyalty Section */}
              <div className={styles.formSection}>
                <p className={styles.formSectionTitle}>⚙️ Abbonamento e Loyalty</p>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="subscriptionPlan">Piano Abbonamento</label>
                    <select
                      id="subscriptionPlan"
                      value={formData.subscriptionPlan}
                      onChange={(e) => setFormData({ ...formData, subscriptionPlan: parseInt(e.target.value) })}
                      className={styles.select}
                    >
                      {Object.entries(SUBSCRIPTION_PLANS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="tenantCode">Codice Tenant</label>
                    <input
                      type="text"
                      id="tenantCode"
                      value={formData.tenantCode}
                      onChange={(e) => setFormData({ ...formData, tenantCode: e.target.value })}
                      className={styles.input}
                    />
                  </div>
                </div>

                <div className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="loyaltyEnabled"
                    checked={formData.loyaltyEnabled}
                    onChange={(e) => setFormData({ ...formData, loyaltyEnabled: e.target.checked })}
                  />
                  <label htmlFor="loyaltyEnabled">Abilita programma Loyalty</label>
                </div>
              </div>

              {error && <div className={styles.error}>❌ {error}</div>}

              <button type="submit" className={styles.submitButton}>
                {editingCompany ? '💾 Salva Modifiche' : '➕ Crea Azienda'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

Companies.displayName = 'Companies';
